import React, { useState } from 'react';

interface CSSReferenceModalProps {
  onClose: () => void;
}

const CSSReferenceModal: React.FC<CSSReferenceModalProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('layout-info');
  const [selectedLayout, setSelectedLayout] = useState('full-layout');

  const layoutVariants = [
    {
      id: 'full-layout',
      name: 'Full 6-Level Layout',
      description: 'Complete layout hierarchy with all optional elements',
      structure: [
        '1. App Shell (.full-area)',
        '2. Body Grid (.body-grid: .menu-area + .page-area)', 
        '3. Page Content (.page-area: .module-row + .module-area)',
        '4. Module Layout (.module-area: .module-content-area + .module-widget-area)',
        '5. Tab System (.module-content-area: .tabs-row + .tab-content-area)',
        '6. Inner Split (.tab-content-area: .left-menu-area + .mid-content-area)'
      ]
    },
    {
      id: 'simple-layout',
      name: 'Simple Layout (No Optional Elements)',
      description: 'Minimal layout with only mandatory elements',
      structure: [
        '1. App Shell (.full-area)',
        '2. Body Grid (.body-grid: .menu-area + .page-area)',
        '3. Page Content (.page-area: .module-row + .module-area)',
        '4. Module Layout (.module-area: .module-content-area only)',
        '5. Tab System (.tab-content-area only)',
        '6. Content Area (.mid-content-area)'
      ]
    },
    {
      id: 'no-tabs-layout',
      name: 'No Tabs Layout',
      description: 'Layout without tab system, direct content',
      structure: [
        '1. App Shell (.full-area)',
        '2. Body Grid (.body-grid)',
        '3. Page Content (.page-area)',
        '4. Module Layout (.module-area + .module-widget-area)',
        '5. Direct Content (.module-content-area)',
        '6. Optional Split (.left-menu-area + .mid-content-area)'
      ]
    }
  ];

  const cssCategories = [
    {
      name: 'Layout Structure',
      classes: [
        { name: '.full-area', purpose: 'Root viewport container', height: '100vh' },
        { name: '.header-row', purpose: 'Fixed header stripe', height: '70px' },
        { name: '.body-grid', purpose: 'Main content grid', height: 'calc(100vh - 70px - 32px)' },
        { name: '.footer-row', purpose: 'Fixed footer stripe', height: '32px' },
        { name: '.menu-area', purpose: 'Left navigation sidebar', width: '60px' },
        { name: '.page-area', purpose: 'Main page content area', width: 'remaining' }
      ]
    },
    {
      name: 'Module Layout',
      classes: [
        { name: '.module-row', purpose: 'Module header stripe', height: '44px' },
        { name: '.module-area', purpose: 'Main working canvas', height: 'remaining' },
        { name: '.module-content-area', purpose: 'Primary content (80%)', width: '4fr' },
        { name: '.module-widget-area', purpose: 'Right sidebar (20%)', width: '320px' }
      ]
    },
    {
      name: 'Tab System',
      classes: [
        { name: '.tabs-row', purpose: 'Tab navigation stripe', height: '44px' },
        { name: '.tab-content-area', purpose: 'Tab content canvas', height: 'remaining' }
      ]
    },
    {
      name: 'Inner Split',
      classes: [
        { name: '.left-menu-area', purpose: 'Left sidebar navigation', width: '260px' },
        { name: '.mid-content-area', purpose: 'Main working area', width: 'remaining' }
      ]
    }
  ];

  const visualStyles = [
    {
      name: 'Card System',
      classes: [
        { name: '.visual-card-large', size: '240px height', use: 'Primary content cards' },
        { name: '.visual-card-medium', size: '200px height', use: 'Standard content cards' },
        { name: '.visual-card-small', size: '180px height', use: 'Compact content cards' },
        { name: '.visual-card-compact', size: '160px height', use: 'Minimal content cards' }
      ]
    },
    {
      name: 'Button Types',
      classes: [
        { name: '.modal-action-btn.update', color: 'Blue', use: 'Update/Save actions' },
        { name: '.modal-action-btn.approve', color: 'Green', use: 'Approve/Confirm actions' },
        { name: '.modal-action-btn.inactivate', color: 'Grey', use: 'Inactivate/Close/Cancel actions' },
        { name: '.modal-action-btn.delete', color: 'Red', use: 'Delete/Remove actions' },
        { name: '.modal-close-button', color: 'White/Yellow on hover', use: 'Modal close button with twist animation' },
        { name: '.btn-add-primary', color: 'Blue', use: 'Add new button (e.g., Add New Item)' }
      ]
    },
    {
      name: 'Search & Input',
      classes: [
        { name: '.control-search-input', use: 'Search bar input styling' },
        { name: '.control-form-input', use: 'Standard text input' },
        { name: '.control-form-select', use: 'Dropdown select' }
      ]
    },
    {
      name: 'Grid System',
      classes: [
        { name: '.layout-grid-2col', columns: '2 columns', responsive: 'Stacks on mobile' },
        { name: '.layout-grid-3col', columns: '3 columns', responsive: 'Stacks on tablet' },
        { name: '.layout-grid-4col', columns: '4 columns', responsive: 'Stacks on medium' }
      ]
    },
    {
      name: 'Status System',
      classes: [
        { name: '.visual-status-success', color: 'Green', use: 'Active/Success states' },
        { name: '.visual-status-warning', color: 'Orange', use: 'Warning/Draft states' },
        { name: '.visual-status-info', color: 'Blue', use: 'Information/Review states' },
        { name: '.visual-status-inactive', color: 'Gray', use: 'Inactive/Disabled states' }
      ]
    },
    {
      name: 'Data Table with Pagination & Scroll ⭐',
      classes: [
        { name: '.layout-data-table-pattern', purpose: 'Main flex column container for table + scroll + pagination pattern' },
        { name: '.container-toolbar.layout-flex-fixed', purpose: 'Fixed height toolbar container (search/actions)' },
        { name: '.container-data-table.layout-flex-content.layout-flex-column', purpose: 'Main table container with vertical flex layout' },
        { name: '.layout-table-scroll-container.layout-flex-content', purpose: 'Scrollable table wrapper with custom scrollbar' },
        { name: '.table-data.layout-table-scrollable', purpose: 'Table with sticky header and horizontal scroll capability' },
        { name: '.layout-pagination-container.layout-flex-fixed', purpose: 'Fixed footer container for pagination controls' },
        { name: '.layout-pagination-controls', purpose: 'Flex container for pagination buttons' },
        { name: '.layout-pagination-info', purpose: 'Text showing current page info (X-Y of Z entries)' },
        { name: '.layout-pagination-button', purpose: 'Individual pagination buttons' },
        { name: '.layout-pagination-button--active', purpose: 'Active/current page button styling' },
        { name: '.interactive-table-row', purpose: 'Hoverable table rows with click handlers' },
        { name: '.visual-status-1', purpose: 'Generic status badge 1 (green/active)' },
        { name: '.visual-status-2', purpose: 'Generic status badge 2 (red/inactive)' },
        { name: '.btn-primary', purpose: 'Primary action buttons (Add New, etc.)' },
        { name: '.btn-secondary', purpose: 'Secondary buttons (Filter, etc.)' },
        { name: '.btn-ghost-small', purpose: 'Small icon-only buttons' },
        { name: '.control-search-field', purpose: 'Search input field container' },
        { name: '.control-search-input', purpose: 'Search input field styling' }
      ]
    }
  ];

  const interactivePatterns = [
      {
        name: 'Modal Overlay',
        demo: (
          <div className="modal-overlay" style={{ position: 'relative', width: 320, height: 180 }}>
            <button className="modal-close-button" style={{ position: 'absolute', right: 8, top: 8 }} title="Close">×</button>
            <div className="modal-content" style={{ width: 300, margin: 'auto', top: 30, position: 'absolute' }}>
              <div className="modal-header">Modal Header</div>
              <div style={{ padding: 16 }}>Modal Content</div>
              <div className="modal-actions">
                <button className="modal-action-btn update">Update</button>
                <button className="modal-action-btn approve">Approve</button>
                <button className="modal-action-btn inactivate">Inactivate</button>
                <button className="modal-action-btn delete">Delete</button>
              </div>
            </div>
          </div>
        )
      },
      {
        name: 'Sticky Modal Footer',
        demo: (
          <div className="modal-content modal-content--detail" style={{ width: 340, height: 220, position: 'relative' }}>
            <button className="modal-close-button" style={{ position: 'absolute', right: 8, top: 8 }} title="Close">×</button>
            <div className="modal-header">Detail Modal Header</div>
            <div style={{ padding: 16, height: 100, overflow: 'auto' }}>
              <p>Lots of content here...</p>
              <p>More content...</p>
              <p>Even more content...</p>
            </div>
            <div className="modal-actions">
              <button className="modal-action-btn update">Update</button>
              <button className="modal-action-btn approve">Approve</button>
              <button className="modal-action-btn inactivate">Inactivate</button>
              <button className="modal-action-btn delete">Delete</button>
            </div>
          </div>
        )
      },
      {
        name: 'Button Types',
        demo: (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', background: '#f8f9fa', padding: 16 }}>
            <button className="modal-action-btn update">Update</button>
            <button className="modal-action-btn approve">Approve</button>
            <button className="modal-action-btn inactivate">Inactivate</button>
            <button className="modal-action-btn delete">Delete</button>
            <button className="btn-add-primary">+ Add New</button>
            <button className="modal-close-button" title="Close">×</button>
          </div>
        )
      },
      {
        name: 'Search Bar',
        demo: (
          <div style={{ background: '#f8f9fa', padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input className="control-search-input" placeholder="Search..." style={{ width: 180 }} />
            <button className="modal-action-btn update">Search</button>
          </div>
        )
      },
      {
        name: 'Data Table',
        demo: (
          <div className="layout-table-scroll-container" style={{ width: 400 }}>
            <table className="layout-table-scrollable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr className="interactive-table-row">
                  <td>Item 1</td>
                  <td><span className="visual-status-1">Active</span></td>
                  <td><button className="modal-action-btn update">Edit</button></td>
                </tr>
                <tr className="interactive-table-row">
                  <td>Item 2</td>
                  <td><span className="visual-status-2">Inactive</span></td>
                  <td><button className="modal-action-btn delete">Delete</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        )
      }
    ];

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '900px', height: '80vh' }}>
        <div className="modal-header">
          <h3 className="text-heading-large">📊 CSS Layout & Design System Reference</h3>
          <button className="modal-close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: '0', height: '100%' }}>
          {/* Tab System */}
          <div className="tabs-row" style={{ paddingLeft: '24px', paddingRight: '24px' }}>
            <button
              className={`interactive-tab-button ${activeTab === 'layout-info' ? 'active' : ''}`}
              onClick={() => setActiveTab('layout-info')}
            >
              🏗️ Layout Structure
            </button>
            <button
              className={`interactive-tab-button ${activeTab === 'visual-system' ? 'active' : ''}`}
              onClick={() => setActiveTab('visual-system')}
            >
              🎨 Visual System
            </button>
            <button
              className={`interactive-tab-button ${activeTab === 'interactive-patterns' ? 'active' : ''}`}
              onClick={() => setActiveTab('interactive-patterns')}
            >
              ⚡ Interactive Patterns
            </button>
            <button
              className={`interactive-tab-button ${activeTab === 'modal-behavior' ? 'active' : ''}`}
              onClick={() => setActiveTab('modal-behavior')}
            >
              🔒 Modal Behavior
            </button>
            <button
              className={`interactive-tab-button ${activeTab === 'height-calculations' ? 'active' : ''}`}
              onClick={() => setActiveTab('height-calculations')}
            >
              📐 Height Calculations
            </button>
          </div>

          <div className="tab-content-area" style={{ padding: '24px' }}>
            
            {activeTab === 'layout-info' && (
              <div>
                <h4 className="text-heading-medium" style={{ marginBottom: '16px' }}>
                  6-Level Layout Hierarchy System
                </h4>
                
                <div className="layout-grid-2col">
                  {/* Layout Variants */}
                  <div>
                    <h5 className="text-heading-small" style={{ marginBottom: '12px' }}>Layout Variants</h5>
                    {layoutVariants.map((variant) => (
                      <div
                        key={variant.id}
                        className={`visual-card-medium interactive-hover-border ${selectedLayout === variant.id ? 'selected-variant' : ''}`}
                        onClick={() => setSelectedLayout(variant.id)}
                        style={{
                          cursor: 'pointer',
                          borderColor: selectedLayout === variant.id ? '#4a90e2' : undefined,
                          marginBottom: '12px'
                        }}
                      >
                        <h6 className="text-heading-small">{variant.name}</h6>
                        <p className="text-label-primary" style={{ fontSize: '12px', marginBottom: '8px' }}>
                          {variant.description}
                        </p>
                        <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#6b7280' }}>
                          {variant.structure.map((level, idx) => (
                            <div key={idx} style={{ marginBottom: '2px' }}>
                              {level}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CSS Classes */}
                  <div>
                    <h5 className="text-heading-small" style={{ marginBottom: '12px' }}>CSS Class Reference</h5>
                    {cssCategories.map((category) => (
                      <div key={category.name} style={{ marginBottom: '16px' }}>
                        <h6 className="text-heading-small" style={{ 
                          backgroundColor: '#f3f4f6', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          marginBottom: '8px'
                        }}>
                          {category.name}
                        </h6>
                        {category.classes.map((cls) => (
                          <div key={cls.name} style={{ 
                            padding: '6px 8px', 
                            borderLeft: '3px solid #e5e7eb',
                            marginBottom: '4px',
                            backgroundColor: '#fafbfc'
                          }}>
                            <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                              <code style={{ fontWeight: '600', color: '#3b82f6' }}>{cls.name}</code>
                              <span className="text-code-mono" style={{ fontSize: '10px' }}>
                                {cls.height || cls.width || ''}
                              </span>
                            </div>
                            <div className="text-label-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
                              {cls.purpose}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'visual-system' && (
              <div>
                <h4 className="text-heading-medium" style={{ marginBottom: '16px' }}>
                  Visual Design System
                </h4>
                
                {visualStyles.map((category) => (
                  <div key={category.name} style={{ marginBottom: '24px' }}>
                    <h5 className="text-heading-small" style={{ marginBottom: '12px' }}>
                      {category.name}
                    </h5>
                    
                    {/* Special handling for Data Table section - render as plain text */}
                    {category.name === 'Data Table with Pagination & Scroll ⭐' ? (
                      <div style={{ 
                        background: '#f8f9fa', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        border: '1px solid #e5e7eb',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        lineHeight: '1.6'
                      }}>
                        {category.classes.map((cls) => (
                          <div key={cls.name} style={{ marginBottom: '8px' }}>
                            <code style={{ fontWeight: '600', color: '#3b82f6' }}>{cls.name}</code> - {'purpose' in cls ? cls.purpose : ('use' in cls ? cls.use : 'No description')}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="layout-grid-3col">
                        {category.classes.map((cls) => (
                          <div key={cls.name} className="visual-card-compact">
                            <div className="visual-card-header">
                              <code style={{ fontSize: '12px', fontWeight: '600' }}>{cls.name}</code>
                            </div>
                            <div style={{ fontSize: '11px' }}>
                              {'size' in cls && <div><strong>Size:</strong> {cls.size}</div>}
                              {'columns' in cls && <div><strong>Columns:</strong> {cls.columns}</div>}
                              {'color' in cls && <div><strong>Color:</strong> {cls.color}</div>}
                              {'use' in cls && <div><strong>Use:</strong> {cls.use}</div>}
                              {'responsive' in cls && <div><strong>Responsive:</strong> {cls.responsive}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'interactive-patterns' && (
              <div>
                <h4 className="text-heading-medium" style={{ marginBottom: '16px' }}>
                  Interactive UI Patterns
                </h4>
                
                {interactivePatterns.map((category) => (
                  <div key={category.name} style={{ marginBottom: '24px' }}>
                    <h5 className="text-heading-small" style={{ marginBottom: '12px' }}>
                      {category.name}
                    </h5>
                    
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#f9fafb' }}>
                      {category.demo}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'modal-behavior' && (
              <div>
                <h4 className="text-heading-medium" style={{ marginBottom: '16px' }}>
                  Enhanced Modal System Behavior
                </h4>
                
                <div className="visual-card-large" style={{ marginBottom: '20px' }}>
                  <div className="visual-card-header">
                    <h5 className="text-heading-small">🔒 Modal UX Principles</h5>
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>1. Forced Focus:</strong> Background is non-interactive when modal is open - users must complete modal action before returning to main interface.
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>2. Visual Clarity:</strong> Background blur (4px) and opacity (0.4) provides context while clearly indicating inactive state.
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>3. Scroll Prevention:</strong> Background content cannot be scrolled when modal is active via <code>body.modal-open</code> class.
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>4. Intentional Actions:</strong> No click-outside closing - users must explicitly use Close button or action buttons.
                    </div>
                    <div>
                      <strong>5. Portal Rendering:</strong> Modals render at document.body level using React Portal for proper z-index layering.
                    </div>
                  </div>
                </div>

                <div className="layout-grid-2col">
                  <div className="visual-card-medium">
                    <div className="visual-card-header">
                      <h5 className="text-heading-small">CSS Implementation</h5>
                    </div>
                    <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                      <div><strong>.modal-overlay:</strong></div>
                      <div>• position: fixed</div>
                      <div>• background: rgba(0,0,0,0.4)</div>
                      <div>• backdrop-filter: blur(4px)</div>
                      <div>• z-index: 2000</div>
                      <div>• animation: modalOverlayFadeIn</div>
                      <div style={{ marginTop: '8px' }}><strong>body.modal-open:</strong></div>
                      <div>• overflow: hidden</div>
                      <div>• touch-action: none</div>
                    </div>
                  </div>

                  <div className="visual-card-medium">
                    <div className="visual-card-header">
                      <h5 className="text-heading-small">Component Usage</h5>
                    </div>
                    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>ModalPortal Component:</strong><br/>
                        Handles all modal rendering via React Portal to document.body level.
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>GenericAddModal Component:</strong><br/>
                        Reusable modal for CSS scaffolding with generic form fields (Label1-4).
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Automatic Scroll Management:</strong><br/>
                        Adds/removes body.modal-open class on mount/unmount.
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Event Isolation:</strong><br/>
                        Prevents click events from reaching background elements.
                      </div>
                      <div>
                        <strong>Animation Lifecycle:</strong><br/>
                        Smooth 0.3s transitions for both overlay and content.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'height-calculations' && (
              <div>
                <h4 className="text-heading-medium" style={{ marginBottom: '16px' }}>
                  Height Calculation System
                </h4>
                
                <div className="visual-card-large" style={{ marginBottom: '20px' }}>
                  <h5 className="text-heading-small" style={{ marginBottom: '12px' }}>
                    Progressive Height Calculation
                  </h5>
                  <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.6' }}>
                    <div><strong>1. Viewport:</strong> <code>100vh</code></div>
                    <div><strong>2. After Header/Footer:</strong> <code>100vh - 70px - 32px</code></div>
                    <div><strong>3. After Module Row:</strong> <code>100vh - 70px - 32px - 44px - 32px</code></div>
                    <div><strong>4. After Content Gaps:</strong> <code>100vh - 70px - 32px - 44px - 32px - 24px</code></div>
                    <div><strong>5. After Tab Row:</strong> <code>100vh - 70px - 32px - 44px - 44px - 32px - 24px</code></div>
                    <div><strong>6. After Inner Padding:</strong> <code>100vh - 70px - 32px - 44px - 44px - 32px - 24px - 12px</code></div>
                  </div>
                </div>

                <div className="layout-grid-2col">
                  <div className="visual-card-medium">
                    <h5 className="text-heading-small">Fixed Dimensions</h5>
                    <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                      <div>Header: <code>70px</code></div>
                      <div>Footer: <code>32px</code></div>
                      <div>Module Row: <code>44px</code></div>
                      <div>Tabs Row: <code>44px</code></div>
                      <div>Menu Width: <code>60px</code></div>
                      <div>Widget Width: <code>320px</code></div>
                      <div>Left Menu: <code>260px</code></div>
                    </div>
                  </div>

                  <div className="visual-card-medium">
                    <h5 className="text-heading-small">CSS Custom Properties</h5>
                    <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                      <div><code>--sz-header: 70px</code></div>
                      <div><code>--sz-footer: 32px</code></div>
                      <div><code>--sz-modulebar: 44px</code></div>
                      <div><code>--sz-rightrail: 320px</code></div>
                      <div><code>--sp-section-gap: 12px</code></div>
                      <div><code>--sp-content-x: 24px</code></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CSSReferenceModal;